import { LockSpec } from '@sparemin/api-sdk';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLockSpec } from 'redux/middleware/api/embed-configuration-service';
import { lockSpecSelector } from 'redux/modules/entities/selectors';
import { Dispatch, State } from 'redux/types';
import { IEmbedConfig } from 'types';

export interface UseEditTemplateModalLockSpecResult {
  /**
   * The lockSpec uuid carried on the project's embed config, if any.  Forward
   * this into `useVideoTemplateEditorState` so the editor's feature pipeline
   * applies the corresponding locks.
   */
  lockSpecUuid: string | undefined;
  /**
   * `true` when there is no lockSpec to load, or when the referenced lockSpec
   * is already in entities.  Use to gate editor mount so users don't see
   * controls flip from unlocked to locked on first paint.
   */
  isLockSpecReady: boolean;
}

/**
 * Loads the lockSpec referenced by `embedConfig.lockSpec.uuid` into the
 * `entities.lockSpecs` slice and reports readiness, so the Quick Editor can
 * mount the UCS with locks already applied.  No-op when the embed config
 * carries no lockSpec.
 */
export default function useEditTemplateModalLockSpec(
  embedConfig: IEmbedConfig | undefined,
): UseEditTemplateModalLockSpecResult {
  const dispatch = useDispatch<Dispatch>();
  const lockSpecUuid = embedConfig?.lockSpec?.uuid;

  const loadedLockSpec = useSelector<State, LockSpec | undefined>(state =>
    lockSpecUuid ? lockSpecSelector(lockSpecUuid)(state) : undefined,
  );

  useEffect(() => {
    if (lockSpecUuid && !loadedLockSpec) {
      dispatch(getLockSpec({ lockSpecId: lockSpecUuid }));
    }
  }, [dispatch, loadedLockSpec, lockSpecUuid]);

  return {
    lockSpecUuid,
    isLockSpecReady: !lockSpecUuid || !!loadedLockSpec,
  };
}
